import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import {
  GetOrderInvoice,
  IInvoice,
} from "../../services/UserSubscriptionServices";
import {
  extractTimeFromDateTime,
  formatCurrency,
  getReadableDateString,
} from "../../utils/commons";

interface InvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: string | null;
}

const separatorSx = {
  borderBottom: "1px dashed #D8D8D8",
  my: 2.5,
};

const InvoiceDialog = ({ open, onClose, orderId }: InvoiceDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState<IInvoice | null>(null);

  useEffect(() => {
    if (!open || !orderId) {
      return;
    }

    setLoading(true);
    GetOrderInvoice(orderId)
      .then(setInvoice)
      .catch(() =>
        enqueueSnackbar("Failed to load invoice", { variant: "error" })
      )
      .finally(() => setLoading(false));
  }, [open, orderId, enqueueSnackbar]);

  const datePart = useMemo(() => {
    if (!invoice?.invoiceDate) return "N/A";
    return getReadableDateString(invoice.invoiceDate) || "N/A";
  }, [invoice?.invoiceDate]);

  const timePart = useMemo(() => {
    if (!invoice?.invoiceDate) return "N/A";
    return extractTimeFromDateTime(invoice.invoiceDate) || "N/A";
  }, [invoice?.invoiceDate]);

  const renderLoading = () => (
    <Box sx={{ p: 2 }}>
      {[...Array(8)].map((_, index) => (
        <Skeleton key={index} height={30} sx={{ mb: 1 }} />
      ))}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 0, bgcolor: "#121212" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton size="small" onClick={onClose} sx={{ color: "#EAEAEA" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          renderLoading()
        ) : !invoice ? (
          <Box sx={{ p: 3 }}>
            <Typography color="#EAEAEA" align="center">
              No invoice data available
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2, pt: 0 }}>
            <Box
              sx={{
                bgcolor: "#F7F7F7",
                borderRadius: 1,
                px: 2.5,
                py: 3,
                color: "#121212",
              }}
            >
              <Typography
                align="center"
                sx={{ fontSize: 36, letterSpacing: 2, lineHeight: 0.6 }}
              >
                ^^^^^^^^^^^^^^^^^^
              </Typography>

              <Typography
                align="center"
                sx={{ fontSize: 34, fontWeight: 700, mt: 0.5 }}
              >
                {invoice.business?.name || "Nikk Fit World"}
              </Typography>
              <Typography
                align="center"
                sx={{
                  mt: 0.5,
                  mb: 2,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: 2,
                  color: "#6A6A6A",
                }}
              >
                OFFICIAL TRANSACTION RECEIPT
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: "#5C5C5C" }}
                >
                  DATE: {datePart.toUpperCase()}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: "#5C5C5C" }}
                >
                  TIME: {timePart}
                </Typography>
              </Box>

              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: "#5C5C5C" }}
              >
                RECEIPT: #{invoice.receiptNumber || invoice.orderId}
              </Typography>

              <Box sx={separatorSx} />

              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: "#2E2E2E" }}
              >
                CUSTOMER DETAILS
              </Typography>
              <Typography sx={{ fontSize: 32, fontWeight: 600, mt: 0.5 }}>
                {invoice.customerName || "N/A"}
              </Typography>
              {invoice.customerPhone ? (
                <Typography sx={{ fontSize: 12, color: "#666" }}>
                  {invoice.customerPhone}
                </Typography>
              ) : null}

              <Box sx={separatorSx} />

              {invoice.lineItems.map((item, index) => (
                <Box key={`${item.name}-${index}`} sx={{ mb: 2.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                        {item.name}
                      </Typography>
                      {item.discount > 0 ? (
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#E65100",
                            fontWeight: 700,
                          }}
                        >
                          ({item.discount}% OFF)
                        </Typography>
                      ) : null}
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#666",
                          fontStyle: "italic",
                          mt: 0.5,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                      {item.discount > 0 ? (
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#888",
                            textDecoration: "line-through",
                          }}
                        >
                          {formatCurrency(item.amount)}
                        </Typography>
                      ) : null}
                      <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                        {formatCurrency(item.total || item.amount)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}

              <Box sx={separatorSx} />

              <Row label="Subtotal" value={formatCurrency(invoice.subtotal)} />

              {invoice.discount > 0 ? (
                <Row
                  label={`Program Discount (${invoice.discount}% OFF)`}
                  value={`-${formatCurrency(
                    Math.max(
                      invoice.subtotal -
                        (invoice.lineItems?.[0]?.total ?? invoice.subtotal),
                      0
                    )
                  )}`}
                />
              ) : null}

              {invoice.couponDiscount > 0 ? (
                <Row
                  label={
                    invoice.couponCode
                      ? `Coupon (${invoice.couponCode})`
                      : "Coupon Discount"
                  }
                  value={`-${formatCurrency(invoice.couponDiscount)}`}
                />
              ) : null}

              <Box sx={{ borderBottom: "1px solid #D0D0D0", my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                  TOTAL AMOUNT
                </Typography>
                <Typography
                  sx={{
                    fontSize: 52,
                    fontWeight: 800,
                    color: "#E65100",
                    lineHeight: 1,
                  }}
                >
                  {formatCurrency(invoice.total)}
                </Typography>
              </Box>

              {invoice.refund ? (
                <Box
                  sx={{
                    mt: 2,
                    px: 1.5,
                    py: 1,
                    bgcolor: "#FFF4E6",
                    border: "1px solid #F8B26A",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: "#B45309" }}
                  >
                    REFUNDED -{" "}
                    {formatCurrency(invoice.refund.refundedAmount || 0)} on{" "}
                    {invoice.refund.refundedAt
                      ? getReadableDateString(invoice.refund.refundedAt)
                      : "N/A"}
                  </Typography>
                </Box>
              ) : null}

              <Typography
                align="center"
                sx={{
                  mt: 4,
                  fontSize: 11,
                  color: "#8A8A8A",
                  letterSpacing: 1.5,
                }}
              >
                THANK YOU FOR BEING PART OF THE ELITE.
              </Typography>

              <Typography
                align="center"
                sx={{ fontSize: 36, letterSpacing: 2, lineHeight: 0.6, mt: 1 }}
              >
                vvvvvvvvvvvvvvvvvv
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
    <Typography sx={{ fontSize: 17, color: "#4D4D4D" }}>{label}</Typography>
    <Typography sx={{ fontSize: 17, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

export default InvoiceDialog;
